import { create } from "zustand"
import type {
	TChatStore,
	TInstructions,
	TSeriesStore,
	TBidRequestFormData,
	TBackendChatHistoryStore,
} from "types/aiAgent.type"

export const useChatStore = create<TChatStore>((set) => ({
	chatHistory: {
		questions: [],
		answers: [],
	},
	addQuestion: (question, type, langsmithTraceId) =>
		set((state) => ({
			chatHistory: {
				...state.chatHistory,
				questions: [...state.chatHistory.questions, { text: question, type, langsmithTraceId }],
			},
		})),
	updateLastQuestionTraceId: (langsmithTraceId) =>
		set((state) => {
			const lastIndex = state.chatHistory.questions.length - 1
			if (lastIndex >= 0) {
				return {
					chatHistory: {
						...state.chatHistory,
						questions: state.chatHistory.questions.map((question, index) =>
							index === lastIndex ? { ...question, langsmithTraceId } : question,
						),
					},
				}
			}
			return state
		}),
	// This code appends a new message to the last answer in the chat history. It does this by mapping over the answers array and concatenating the new message to the last answer, leaving the other answers unchanged.
	addAnswer: (message) =>
		set((state) => {
			// Only proceed if message is defined and not null
			if (message === undefined || message === null) {
				return state
			}

			const questionCount = state.chatHistory.questions.length
			const answerCount = state.chatHistory.answers.length

			// If there's no answer for the last question, create a new one
			if (answerCount < questionCount) {
				return {
					chatHistory: {
						...state.chatHistory,
						answers: [...state.chatHistory.answers, { type: "text", content: message }],
					},
				}
			}

			// Otherwise, append to the last answer
			const lastIndex = answerCount - 1
			return {
				chatHistory: {
					...state.chatHistory,
					answers: state.chatHistory.answers.map((answer, index) =>
						index === lastIndex ? { ...answer, content: answer.content + message } : answer,
					),
				},
			}
		}),
	setFailedAnswer: () =>
		set((state) => {
			const questionCount = state.chatHistory.questions.length
			const answerCount = state.chatHistory.answers.length

			// If there's no answer for the last question, create a new one with FAILED status
			if (answerCount < questionCount) {
				return {
					chatHistory: {
						...state.chatHistory,
						answers: [...state.chatHistory.answers, { type: "text", content: "FAILED" }],
					},
				}
			}

			// Otherwise, update the last answer to FAILED
			const lastIndex = answerCount - 1
			return {
				chatHistory: {
					...state.chatHistory,
					answers: state.chatHistory.answers.map((answer, index) =>
						index === lastIndex ? { ...answer, content: "FAILED" } : answer,
					),
				},
			}
		}),
	clearChatHistory: () => set({ chatHistory: { questions: [], answers: [] } }),

	// Enhanced methods for component visibility
	markLastAnswerForSeriesSelector: () =>
		set((state) => {
			const questionCount = state.chatHistory.questions.length
			const answerCount = state.chatHistory.answers.length

			// If there's no answer for the last question, create one with the series selector marker
			if (answerCount < questionCount) {
				return {
					chatHistory: {
						...state.chatHistory,
						answers: [
							...state.chatHistory.answers,
							{
								type: "text",
								content: "",
								showSeriesSelector: true,
							},
						],
					},
				}
			}

			// Otherwise, mark the last answer
			const lastIndex = answerCount - 1
			if (lastIndex >= 0) {
				return {
					chatHistory: {
						...state.chatHistory,
						answers: state.chatHistory.answers.map((answer, index) =>
							index === lastIndex
								? {
										...answer,
										showSeriesSelector: true,
									}
								: answer,
						),
					},
				}
			}
			return state
		}),

	markLastAnswerForBidRequestForm: () =>
		set((state) => {
			const questionCount = state.chatHistory.questions.length
			const answerCount = state.chatHistory.answers.length

			// If there's no answer for the last question, create one with the bid request marker
			if (answerCount < questionCount) {
				return {
					chatHistory: {
						...state.chatHistory,
						answers: [
							...state.chatHistory.answers,
							{
								type: "text",
								content: "",
								showBidRequestForm: true,
							},
						],
					},
				}
			}

			// Otherwise, mark the last answer
			const lastIndex = answerCount - 1
			if (lastIndex >= 0) {
				return {
					chatHistory: {
						...state.chatHistory,
						answers: state.chatHistory.answers.map((answer, index) =>
							index === lastIndex
								? {
										...answer,
										showBidRequestForm: true,
									}
								: answer,
						),
					},
				}
			}
			return state
		}),

	clearComponentMarkers: () =>
		set((state) => ({
			chatHistory: {
				...state.chatHistory,
				answers: state.chatHistory.answers.map((answer) => ({
					...answer,
					showSeriesSelector: false,
					showBidRequestForm: false,
				})),
			},
		})),
	toolCallInfo: {},
	setToolCallInfo: (newToolCallInfo) =>
		set((state) => ({
			toolCallInfo: { ...state.toolCallInfo, ...newToolCallInfo },
		})),

	isChatOpen: false,
	toggleChat: () => set((state) => ({ isChatOpen: !state.isChatOpen })),
	openChat: () => set({ isChatOpen: true }),
	closeChat: () => set({ isChatOpen: false }),
}))

// Add new instruction
const initialInstruction = {
	questionId: null,
	question: "",
	prompt: "",
	sections: [],
}

export const useInstructionsStore = create<TInstructions>((set) => ({
	instruction: initialInstruction,

	setInstruction: (updates) =>
		set((state) => ({
			instruction: { ...state.instruction, ...updates },
		})),

	clearInstructions: () =>
		set({
			instruction: initialInstruction,
		}),
}))

export const useSeriesStore = create<TSeriesStore>((set, get) => ({
	activeSeries: {
		available: null,
		extracted: "",
		isActive: false,
	},
	selectedSeries: "",
	submittedSeriesByAnswerIndex: {},

	setSubmittedSeries: (answerIndex, series) =>
		set((state) => ({
			submittedSeriesByAnswerIndex: {
				...state.submittedSeriesByAnswerIndex,
				[answerIndex]: series,
			},
		})),

	isSeriesSubmitted: (answerIndex, selectedSeries) => {
		const { submittedSeriesByAnswerIndex } = get()
		const submittedSeries = submittedSeriesByAnswerIndex[answerIndex]
		return submittedSeries !== undefined && selectedSeries === submittedSeries
	},

	setActiveSeries: (series) =>
		set({
			activeSeries: {
				available: series.availableSeries,
				extracted: series.extractedSeries,
				isActive: true,
			},
		}),

	clearActiveSeries: () =>
		set({
			activeSeries: {
				available: null,
				extracted: "",
				isActive: false,
			},
			submittedSeriesByAnswerIndex: {},
		}),

	// Check if series selector should be shown
	hasActiveSeries: () => get().activeSeries.isActive && get().activeSeries.available !== null,

	// Get current active series
	getActiveSeries: () => get().activeSeries.available,

	getExtractedSeries: () => get().activeSeries.extracted,

	setSelectedSeries: (series) => set({ selectedSeries: series }),
}))

export const useBidRequestFormData = create<TBidRequestFormData>((set, get) => ({
	activeBidRequest: {
		extractedFields: null,
		availableFields: null,
		isActive: false,
	},
	submittedBidRequestsByAnswerIndex: {},

	setActiveBidRequest: (formData) =>
		set({
			activeBidRequest: {
				extractedFields: formData.extractedFields,
				availableFields: formData.availableFields,
				isActive: true,
			},
		}),

	clearActiveBidRequest: () =>
		set({
			activeBidRequest: {
				extractedFields: null,
				availableFields: null,
				isActive: false,
			},
			submittedBidRequestsByAnswerIndex: {},
		}),

	// Check if bid request form should be shown
	hasActiveBidRequest: () =>
		get().activeBidRequest.isActive &&
		get().activeBidRequest.extractedFields !== null &&
		get().activeBidRequest.availableFields !== null,

	// Get current active bid request data
	getActiveBidRequest: () => {
		const active = get().activeBidRequest
		if (!active.isActive) return null
		return {
			extractedFields: active.extractedFields,
			availableFields: active.availableFields,
		}
	},

	setSubmittedBidRequest: (answerIndex) =>
		set((state) => ({
			submittedBidRequestsByAnswerIndex: {
				...state.submittedBidRequestsByAnswerIndex,
				[answerIndex]: true,
			},
		})),

	isBidRequestSubmitted: (answerIndex) => {
		const { submittedBidRequestsByAnswerIndex } = get()
		return submittedBidRequestsByAnswerIndex[answerIndex] === true
	},
}))

export const useBackendChatHistoryStore = create<TBackendChatHistoryStore>((set) => ({
	backendChatHistory: [],

	addBackendChatMessage: (message) =>
		set((state) => ({
			backendChatHistory: [...state.backendChatHistory, message],
		})),

	replaceBackendChatHistory: (history) =>
		set({
			backendChatHistory: history,
		}),

	clearBackendChatHistory: () =>
		set({
			backendChatHistory: [],
		}),
}))
